import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { OnboardingChecklist } from '@/components/shared/OnboardingChecklist';
import { 
  DollarSign, TrendingUp, TrendingDown, Users, Target, Zap, Eye, MousePointer, 
  Send, Activity, Award, BarChart3, PieChart, Percent, CalendarIcon, 
  ChevronUp, ChevronDown, Crown, Medal, Trophy, Sparkles, FileText, ChevronRight, AlertTriangle, Lightbulb, Building2
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { format, startOfMonth, subDays, startOfWeek, endOfWeek } from 'date-fns';
const API = `${BACKEND_URL}/api`;

// IB Colors - Vibrant colors for all 24 IBs
const IB_COLORS = {
  'FIRA INDI': '#E63946',      // Red
  'FGC': '#10B981',            // Emerald
  'MBE CAPITAL': '#3B82F6',    // Blue
  'SBE': '#F59E0B',            // Amber
  'CIKO SANG SULTAN': '#8B5CF6', // Purple
  'QISSANOVA': '#EC4899',      // Pink
  'FAIZ ISMAIL': '#06B6D4',    // Cyan
  'SJ SETUP': '#14B8A6',       // Teal
  'MISYA LIQUIDITY': '#F97316', // Orange
  'GOLDHUB': '#EAB308',        // Yellow
  'ZACK ZONE': '#84CC16',      // Lime
  'HZ ANALYST': '#A855F7',     // Violet
  'PROF KHAN': '#22D3EE',      // Sky
  'POCKET ACADEMY': '#FB7185', // Rose
  'NEURX': '#4ADE80',          // Green
  'TICER JULYA': '#C084FC',    // Fuchsia
  'FR TRADING': '#60A5FA',     // Light Blue
  'XTRADE': '#FBBF24',         // Gold
  'MAL HIGH RISK': '#F472B6',  // Light Pink
  'AMAADTJ': '#2DD4BF',        // Light Teal
  'LYNDA EXCHANGE': '#A78BFA', // Light Purple
  'FIRA IND': '#E63946',       // Alias
  'FGC Trading': '#10B981',    // Alias
  'MBE Capital': '#3B82F6',    // Alias
};

// Broker Colors (from Channel Profile)
/**
 * OVERALL PERFORMANCE - LUXURY DESIGN V3
 * With Contributions by IB, Performance by Broker, Top Rankings, Performance Tracker
 */

// Standardized Section Header Component
function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white font-heading">{title}</h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

// Get performance tier
const getPerformanceTier = (percent) => {
  const p = parseFloat(percent);
  if (p >= 100) return { tier: 'good', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.06)', borderColor: 'rgba(16, 185, 129, 0.25)' };
  if (p >= 80) return { tier: 'average', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.06)', borderColor: 'rgba(245, 158, 11, 0.25)' };
  return { tier: 'poor', color: '#E63946', bgColor: 'rgba(230, 57, 70, 0.06)', borderColor: 'rgba(230, 57, 70, 0.25)' };
};

// ROI tier colors
const getRoiTier = (r) => {
  if (r < 1) return { tier: 'BAD', color: '#E63946', bg: 'rgba(230, 57, 70, 0.15)' };
  if (r < 2) return { tier: 'AVERAGE', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)' };
  if (r < 3) return { tier: 'GOOD', color: '#10B981', bg: 'rgba(16, 185, 129, 0.15)' };
  return { tier: 'EXCELLENT', color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.15)' };
};

// Get progress color based on percentage
const getProgressColor = (percent) => {
  if (percent >= 80) return '#10B981';
  if (percent >= 50) return '#F59E0B';
  return '#E63946';
};

// Calculate tracker metrics
const calculateTrackerMetrics = (metric, currentDay, daysInMonth, daysRemaining) => {
  const percentComplete = ((metric.current / metric.target) * 100).toFixed(1);
  const shortfall = metric.target - metric.current;
  const expectedByNow = (metric.target / daysInMonth) * currentDay;
  const daysBehind = Math.max(0, Math.ceil((expectedByNow - metric.current) / (metric.target / daysInMonth)));
  const nextDayTarget = daysRemaining > 0 ? Math.ceil((metric.target - metric.current) / daysRemaining) : 0;
  
  return {
    percentComplete: parseFloat(percentComplete),
    shortfall: shortfall > 0 ? shortfall : 0,
    surplus: shortfall < 0 ? Math.abs(shortfall) : 0,
    daysBehind,
    nextDayTarget,
    expectedByNow: Math.round(expectedByNow)
  };
};

// Generate daily data for insights
const generateDailyData = (metric, currentDay, daysInMonth, currentMonth = 1) => {
  if (!metric) return [];
  const days = [];
  const dailyTarget = metric.target / daysInMonth;
  let cumulative = 0;
  
  for (let day = 1; day <= daysInMonth; day++) {
    const variance = (Math.sin(day * 100) * 10000 - Math.floor(Math.sin(day * 100) * 10000) - 0.5) * 0.4;
    const actualDaily = day <= currentDay ? Math.round(dailyTarget * (1 + variance)) : null;
    if (actualDaily !== null) cumulative += actualDaily;
    const targetCumulative = Math.round(dailyTarget * day);
    
    days.push({
      day,
      date: `${day}/${currentMonth}`,
      actual: actualDaily !== null ? cumulative : null,
      target: targetCumulative,
      dailyActual: actualDaily,
      dailyTarget: Math.round(dailyTarget),
      variance: actualDaily !== null ? cumulative - targetCumulative : null,
      variancePercent: actualDaily !== null ? (((cumulative - targetCumulative) / targetCumulative) * 100).toFixed(1) : null
    });
  }
  return days;
};

export default function OverallPerformancePage() {
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState({});
  const [loading, setLoading] = useState(true);
  const [aggregatedTargets, setAggregatedTargets] = useState(null);
  const navigate = useNavigate();
  
  // Date range state - use useState for stable dates
  const [currentDate] = useState(() => new Date());
  const [defaultStartDate] = useState(() => startOfMonth(new Date()));
  const [defaultEndDate] = useState(() => new Date());
  
  const [dateRange, setDateRange] = useState('mtd');
  const [customDateFrom, setCustomDateFrom] = useState(null);
  const [customDateTo, setCustomDateTo] = useState(null);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [scenario, setScenario] = useState('base');
  const [adjustment, setAdjustment] = useState(0);
  const [forecastPeriod, setForecastPeriod] = useState('month');
  
  // Modal states
  const [rankingModalOpen, setRankingModalOpen] = useState(false);
  const [selectedRankingCategory, setSelectedRankingCategory] = useState(null);
  const [insightModalOpen, setInsightModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryType, setSummaryType] = useState('weekly');
  const [summaryWeek, setSummaryWeek] = useState(0);
  const [warRoomHQ, setWarRoomHQ] = useState(null); // 0=current week, 1=week1, 2=week2...

  // Generate trend data from REAL daily_trends data from API
  const trendDataMarketing = useMemo(() => {
    const trends = data?.daily_trends || [];
    if (trends.length === 0) return [];
    return trends.map(d => ({
      date: d.date ? d.date.substring(5) : '',
      spend: Math.round(d.spend || 0),
      subscribers: Math.round(d.subscribers || 0),
      leads: Math.round(d.leads || 0),
      closed: Math.round(d.closed || 0),
    }));
  }, [data?.daily_trends]);

  const trendDataTrading = useMemo(() => {
    const trends = data?.daily_trends || [];
    if (trends.length === 0) return [];
    return trends.map(d => ({
      date: d.date ? d.date.substring(5) : '',
      activeClient: Math.round(d.active_client || 0),
      tradedLot: parseFloat((d.traded_lot || 0).toFixed(1)),
      commissions: Math.round(d.commissions || 0),
    }));
  }, [data?.daily_trends]);
  const [ibContributionModalOpen, setIbContributionModalOpen] = useState(false);
  const [selectedIbCategory, setSelectedIbCategory] = useState(null);

  // Calculate dateFrom and dateTo based on dateRange - using string keys for stability
  const dateRangeKey = useMemo(() => {
    if (dateRange === 'custom' && customDateFrom && customDateTo) {
      return `custom-${customDateFrom.getTime()}-${customDateTo.getTime()}`;
    }
    return dateRange;
  }, [dateRange, customDateFrom, customDateTo]);

  const { dateFrom, dateTo } = useMemo(() => {
    const now = currentDate;
    
    switch(dateRange) {
      case 'today': return { dateFrom: now, dateTo: now };
      case 'yesterday': return { dateFrom: subDays(now, 1), dateTo: subDays(now, 1) };
      case 'this-week': return { dateFrom: startOfWeek(now, { weekStartsOn: 1 }), dateTo: now };
      case 'last-week': const lws = startOfWeek(subDays(now, 7), { weekStartsOn: 1 }); return { dateFrom: lws, dateTo: endOfWeek(lws, { weekStartsOn: 1 }) };
      case 'this-month':
      case 'mtd': return { dateFrom: startOfMonth(now), dateTo: now };
      case 'last-month': 
        // Last month = January 2026 (month before current Feb 2026)
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        return { dateFrom: lastMonth, dateTo: lastMonthEnd };
      case 'full-2025':
        return { dateFrom: new Date(2025, 0, 1), dateTo: new Date(2025, 11, 31) };
      case 'full-2026':
        return { dateFrom: new Date(2026, 0, 1), dateTo: new Date(2026, 11, 31) };
      case 'full': return { dateFrom: new Date(2025, 0, 1), dateTo: now };
      case 'custom': return { dateFrom: customDateFrom || defaultStartDate, dateTo: customDateTo || defaultEndDate };
      default: return { dateFrom: defaultStartDate, dateTo: defaultEndDate };
    }
  }, [dateRange, customDateFrom, customDateTo, defaultStartDate, defaultEndDate, currentDate]);

  // Fetch data when dateRangeKey changes (not dateFrom/dateTo objects)
  useEffect(() => {
    fetchData();
  }, [dateRangeKey]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      
      // Format dates for API
      const dateFromStr = format(dateFrom, 'yyyy-MM-dd');
      const dateToStr = format(dateTo, 'yyyy-MM-dd');
      
      // Also fetch same date range from LAST MONTH for comparison
      const dayOfMonth = dateTo.getDate();
      const lastMonthFrom = new Date(dateFrom.getFullYear(), dateFrom.getMonth() - 1, 1);
      const lastMonthTo = new Date(dateFrom.getFullYear(), dateFrom.getMonth() - 1, Math.min(dayOfMonth, new Date(dateFrom.getFullYear(), dateFrom.getMonth(), 0).getDate()));
      const lastFromStr = format(lastMonthFrom, 'yyyy-MM-dd');
      const lastToStr = format(lastMonthTo, 'yyyy-MM-dd');
      
      const [response, prevResponse] = await Promise.all([
        axios.get(`${API}/performance/overall`, { headers: { Authorization: `Bearer ${token}` }, params: { date_from: dateFromStr, date_to: dateToStr } }),
        axios.get(`${API}/performance/overall`, { headers: { Authorization: `Bearer ${token}` }, params: { date_from: lastFromStr, date_to: lastToStr } }).catch(() => ({ data: {} })),
      ]);
      setData(response.data);
      setPrevData(prevResponse.data || {});
      
      // Fetch aggregated targets for current month
      const targetMonth = dateTo.getMonth() + 1;
      const targetYear = dateTo.getFullYear();
      try {
        const tgtRes = await axios.get(`${API}/aggregated-targets/${targetYear}/${targetMonth}`, { headers: { Authorization: `Bearer ${token}` } });
        if (tgtRes.data?.success) setAggregatedTargets(tgtRes.data.targets);
      } catch { /* use defaults if no targets set */ }
    } catch (error) {
      toast.error('Failed to load performance data');
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#6B7280]">Loading performance data...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Calculate projections
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const daysPassed = currentDate.getDate();
  const remainingDays = daysInMonth - daysPassed;
  
  // Projection data - use REAL aggregated targets from all channels, fallback to defaults
  const tgt = aggregatedTargets || {};
  const projectionData = [
    { key: 'subscribers', label: 'Subscribers', current: data.global_metrics.total_subscribers, target: tgt.subscribers || 2500, dailyAvg: data.global_metrics.total_subscribers / Math.max(daysPassed, 1), icon: Users, iconColor: '#3B82F6', remainingDays },
    { key: 'leads', label: 'Leads', current: data.global_metrics.total_leads || 0, target: tgt.leads || 800, dailyAvg: (data.global_metrics.total_leads || 0) / Math.max(daysPassed, 1), icon: Target, iconColor: '#E63946', remainingDays },
    { key: 'closed', label: 'Closed', current: data.global_metrics.total_closed || 0, target: tgt.closed || 120, dailyAvg: (data.global_metrics.total_closed || 0) / Math.max(daysPassed, 1), icon: Zap, iconColor: '#10B981', remainingDays },
    { key: 'activeClient', label: 'Active Client', current: data.global_metrics.total_active_client, target: tgt.activeClient || 55, dailyAvg: data.global_metrics.total_active_client / Math.max(daysPassed, 1), icon: Users, iconColor: '#F59E0B', remainingDays },
    { key: 'tradedLot', label: 'Traded Lot', current: data.global_metrics.total_traded_lot, target: tgt.tradedLot || 100, dailyAvg: data.global_metrics.total_traded_lot / Math.max(daysPassed, 1), icon: TrendingUp, iconColor: '#F97316', remainingDays },
    { key: 'commissions', label: 'Commissions', current: data.global_metrics.total_commission, target: tgt.commission || 15000, dailyAvg: data.global_metrics.total_commission / Math.max(daysPassed, 1), icon: DollarSign, iconColor: '#8B5CF6', prefix: 'RM', remainingDays },
  ];

  const getProjection = (item) => {
    const adjustmentFactor = 1 + (adjustment / 100);
    const projected = item.current + (item.dailyAvg * item.remainingDays * adjustmentFactor);
    const currentPercent = ((item.current / item.target) * 100).toFixed(1);
    const projectedPercent = ((projected / item.target) * 100).toFixed(1);
    return { projected, currentPercent, projectedPercent };
  };

  // Use REAL data from API - no more multipliers for core metrics
  const totalSpend = Math.round(data.global_metrics.total_spend || 0);
  const totalCommission = Math.round(data.global_metrics.total_commission || 0);
  const totalDeposit = Math.round(data.global_metrics.total_deposit || 0);
  const totalTradedLot = data.global_metrics.total_traded_lot || 0;
  const totalActiveClient = data.global_metrics.total_active_client || 0;
  const totalSubscribers = data.global_metrics.total_subscribers || 0;
  const comPerLot = data.global_metrics.com_per_lot || 0;
  
  // Additional metrics from API - LPV, CTA, Leads, Closed
  const totalLpv = Math.round(data.global_metrics.total_lpv || 0);
  const totalCta = Math.round(data.global_metrics.total_cta || 0);
  const totalLeads = Math.round(data.global_metrics.total_leads || 0);
  const totalClosed = Math.round(data.global_metrics.total_closed || 0);
  
  // These metrics don't exist in current data structure
  const totalImpressions = 0;
  const totalLinkClicks = 0;
  const totalSignal = 0;
  const totalIbActivity = 0;
  
  const roi = totalSpend > 0 ? ((totalCommission - totalSpend) / totalSpend) : 0;
  const roiTier = getRoiTier(roi);

  // Helper function to format metric value - show "N/A" or actual value
  const formatMetricValue = (value, prefix = '', suffix = '') => {
    if (value === null || value === undefined) {
      return { display: '-', hasData: false };
    }
    if (value === 0) {
      return { display: `${prefix}0${suffix}`, hasData: true };
    }
    return { display: `${prefix}${value.toLocaleString()}${suffix}`, hasData: true };
  };

  // Compute REAL trends by comparing current vs last month same period
  const prevGm = prevData?.global_metrics || {};
  const calcTrend = (current, prevVal) => {
    if (!prevVal || prevVal === 0) return current > 0 ? 100 : 0;
    return ((current - prevVal) / prevVal * 100);
  };
  
  // Key metrics scorecard data - ALL metrics with REAL trends
  const performanceMetrics = [
    { label: 'Total Spend', ...formatMetricValue(totalSpend, 'RM'), trend: calcTrend(totalSpend, prevGm.total_spend), icon: DollarSign, color: '#E63946' },
    { label: 'Impressions', ...formatMetricValue(totalImpressions), trend: calcTrend(totalImpressions, prevGm.total_impressions), icon: Eye, color: '#3B82F6' },
    { label: 'Link Clicks', ...formatMetricValue(totalLinkClicks), trend: calcTrend(totalLinkClicks, prevGm.total_link_clicks), icon: MousePointer, color: '#F97316' },
    { label: 'LPV', ...formatMetricValue(totalLpv), trend: calcTrend(totalLpv, prevGm.total_lpv), icon: Eye, color: '#10B981' },
    { label: 'CTA Clicks', ...formatMetricValue(totalCta), trend: calcTrend(totalCta, prevGm.total_cta), icon: MousePointer, color: '#F59E0B' },
    { label: 'Subs Free Trial', display: (data.global_metrics.total_subs_free_trial || 0) > 0 ? (data.global_metrics.total_subs_free_trial || 0).toLocaleString() : '-', hasData: (data.global_metrics.total_subs_free_trial || 0) > 0, trend: calcTrend(data.global_metrics.total_subs_free_trial || 0, prevGm.total_subs_free_trial || 0), icon: Users, color: '#EC4899' },
    { label: 'Subscribers', ...formatMetricValue(totalSubscribers), trend: calcTrend(totalSubscribers, prevGm.total_subscribers), icon: Users, color: '#3B82F6' },
    { label: 'Leads', ...formatMetricValue(totalLeads), trend: calcTrend(totalLeads, prevGm.total_leads), icon: Target, color: '#E63946' },
    { label: 'Closed', ...formatMetricValue(totalClosed), trend: calcTrend(totalClosed, prevGm.total_closed), icon: Zap, color: '#10B981' },
    { label: 'Deposit', ...formatMetricValue(totalDeposit, '$'), trend: calcTrend(totalDeposit, prevGm.total_deposit), icon: DollarSign, color: '#F59E0B' },
    { label: 'Signal', ...formatMetricValue(totalSignal), trend: calcTrend(totalSignal, prevGm.total_signal), icon: Send, color: '#06B6D4' },
    { label: 'IB Activity', ...formatMetricValue(totalIbActivity), trend: calcTrend(totalIbActivity, prevGm.total_ib_activity), icon: Activity, color: '#8B5CF6' },
    { label: 'Active Client', ...formatMetricValue(totalActiveClient), trend: calcTrend(totalActiveClient, prevGm.total_active_client), icon: Users, color: '#F97316' },
    { label: 'Traded Lot', display: totalTradedLot > 0 ? totalTradedLot.toFixed(1) : '-', hasData: totalTradedLot > 0, trend: calcTrend(totalTradedLot, prevGm.total_traded_lot), icon: TrendingUp, color: '#10B981' },
    { label: 'Commissions', ...formatMetricValue(totalCommission, 'RM'), trend: calcTrend(totalCommission, prevGm.total_commission), icon: DollarSign, color: '#E63946' },
    { label: 'Com/Lot', display: comPerLot > 0 ? `$${comPerLot.toFixed(2)}` : '-', hasData: comPerLot > 0, trend: calcTrend(comPerLot, prevGm.com_per_lot), icon: TrendingUp, color: comPerLot >= 12 ? '#10B981' : comPerLot >= 8 ? '#F59E0B' : '#EF4444' },
    { label: 'ROI', display: totalSpend > 0 ? roi.toFixed(2) : '-', hasData: totalSpend > 0, trend: 0, icon: Award, color: roiTier.color, isRoi: true, roiTier },
  ];

  // IB performance data (Contributions by IB) - using REAL data from API
  // Sort by value descending and prepare both top 5 and full data
  const getIbCategoryData = (dataArr) => {
    const sortedData = [...dataArr].sort((a, b) => b.value - a.value);
    // Assign vibrant colors based on ranking
    const rankColors = ['#E63946', '#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#14B8A6', '#F97316', '#EAB308', '#84CC16', '#A855F7', '#22D3EE', '#FB7185', '#4ADE80', '#C084FC', '#60A5FA', '#FBBF24', '#F472B6', '#2DD4BF', '#A78BFA', '#34D399', '#FB923C', '#38BDF8'];
    const coloredData = sortedData.map((item, idx) => ({
      ...item,
      color: IB_COLORS[item.name] || rankColors[idx % rankColors.length]
    }));
    return {
      top5: coloredData.slice(0, 5),
      full: coloredData
    };
  };
  
  // Use REAL data from API - no multipliers
  const ibCategoriesRaw = [
    { key: 'deposit', title: 'DEPOSIT', prefix: '$', data: data.ib_performance.map(ib => ({ name: ib.ib_name, value: Math.round(ib.deposit || 0) })) },
    { key: 'activeClient', title: 'ACTIVE CLIENT', data: data.ib_performance.map(ib => ({ name: ib.ib_name, value: Math.round(ib.active_client || 0) })) },
    { key: 'lotTraded', title: 'LOT TRADED', data: data.ib_performance.map(ib => ({ name: ib.ib_name, value: Math.round((ib.traded_lot || 0) * 10) / 10 })) },
    { key: 'commissions', title: 'COMMISSIONS', prefix: 'RM ', data: data.ib_performance.map(ib => ({ name: ib.ib_name, value: Math.round(ib.commission || 0) })) },
    { key: 'comPerLot', title: 'COM/LOT', prefix: '$', data: data.ib_performance.map(ib => ({ name: ib.ib_name, value: ib.com_per_lot || 0 })).filter(d => d.value > 0) },
  ];
  
  // Add top5 and full data to each category with vibrant colors
  const ibCategories = ibCategoriesRaw.map(cat => ({
    ...cat,
    ...getIbCategoryData(cat.data)
  }));

  // Broker performance data (Performance by Brokers) - REAL data from API
  const BROKER_COLOR_MAP = {
    'HFM': '#E63946', 'OCTA': '#00A650', 'VANTAGE': '#0052FF', 'IC MARKETS': '#F59E0B',
    'EXNESS': '#8B5CF6', 'XM': '#3B82F6', 'FXCM': '#06B6D4', 'PEPPERSTONE': '#EC4899',
    'VALETAX': '#14B8A6', 'IC_MARKETS': '#F59E0B',
  };
  const defaultBrokerColors = ['#E63946', '#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#14B8A6', '#F97316'];
  const rawBrokers = data.broker_performance || [];
  const brokerWithColors = rawBrokers.map((b, idx) => ({
    ...b,
    color: BROKER_COLOR_MAP[b.broker_name] || defaultBrokerColors[idx % defaultBrokerColors.length]
  }));

  const brokerCategories = [
    { key: 'deposit', title: 'DEPOSIT', prefix: '$', data: brokerWithColors.map(b => ({ name: b.broker_name, value: b.deposit, color: b.color })).filter(b => b.value > 0).sort((a,b) => b.value - a.value) },
    { key: 'activeClient', title: 'ACTIVE CLIENT', data: brokerWithColors.map(b => ({ name: b.broker_name, value: b.active_client, color: b.color })).filter(b => b.value > 0).sort((a,b) => b.value - a.value) },
    { key: 'lotTraded', title: 'LOT TRADED', data: brokerWithColors.map(b => ({ name: b.broker_name, value: b.traded_lot, color: b.color })).filter(b => b.value > 0).sort((a,b) => b.value - a.value) },
    { key: 'commissions', title: 'COMMISSIONS', prefix: 'RM ', data: brokerWithColors.map(b => ({ name: b.broker_name, value: b.commission, color: b.color })).filter(b => b.value > 0).sort((a,b) => b.value - a.value) },
    { key: 'comPerLot', title: 'COM/LOT', prefix: '$', data: brokerWithColors.map(b => ({ name: b.broker_name, value: b.com_per_lot || 0, color: b.color })).filter(b => b.value > 0).sort((a,b) => b.value - a.value) },
  ];

  // Top rankings data
  const topByCommission = [...data.ib_performance].sort((a, b) => b.commission - a.commission);
  const topByLot = [...data.ib_performance].sort((a, b) => b.traded_lot - a.traded_lot);
  const topByDeposit = [...data.ib_performance].sort((a, b) => b.deposit - a.deposit);
  const topByClient = [...data.ib_performance].sort((a, b) => b.active_client - a.active_client);

  // Performance Tracker metrics - using REAL data from API
  const trackerMetrics = [
    { key: 'totalSpend', label: 'Total Spend', current: totalSpend, target: 150000, dailyTarget: 5000, icon: DollarSign, color: '#E63946', prefix: 'RM' },
    { key: 'subscribers', label: 'Subscribers', current: totalSubscribers, target: 25000, dailyTarget: 833, icon: Users, color: '#10B981' },
    { key: 'deposit', label: 'Deposit', current: totalDeposit, target: 500000, dailyTarget: 16667, icon: DollarSign, color: '#F97316', prefix: '$' },
    { key: 'activeClient', label: 'Active Client', current: totalActiveClient, target: 2000, dailyTarget: 67, icon: Users, color: '#3B82F6' },
    { key: 'tradedLot', label: 'Traded Lot', current: Math.round(totalTradedLot * 10) / 10, target: 5000, dailyTarget: 167, icon: TrendingUp, color: '#10B981' },
    { key: 'commissions', label: 'Commissions', current: totalCommission, target: 500000, dailyTarget: 16667, icon: DollarSign, color: '#E63946', prefix: 'RM' },
  ];

  const currentDay = currentDate.getDate();
  const daysRemaining = daysInMonth - currentDay;

  // Custom Tooltip for charts with visible colors
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#1A1A22] border border-[#252530] rounded-lg p-3 shadow-xl">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: data.color }} />
            <p className="text-white font-semibold text-sm">{data.name}</p>
          </div>
          <p className="text-[#9CA3AF] text-xs">{payload[0].name}: <span className="text-white font-mono">{data.value?.toLocaleString()}</span></p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveLayout>
      <div className="flex flex-col min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))]">
        {/* Sticky Header */}
        <header className="sticky top-0 z-30 bg-[hsl(var(--surface))]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
          <div className="px-4 lg:px-6 py-3 lg:py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h1 className="text-lg lg:text-xl font-bold tracking-tight text-[hsl(var(--text-primary))]" data-testid="page-title">Overall Performance</h1>
              <p className="text-xs text-[hsl(var(--text-muted))]">Executive Dashboard • All IBs Combined</p>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              {/* Summary Report Button */}
              <Button 
                variant="outline" 
                className="bg-[#E63946]/10 border-[#E63946]/40 text-[#E63946] hover:bg-[#E63946]/20 text-xs font-semibold"
                onClick={() => {
                  setSummaryModalOpen(true);
                  // Fetch War Room HQ data for health scores
                  if (!warRoomHQ) {
                    const token = localStorage.getItem('token');
                    axios.get(`${API}/war-room-hq`, { headers: { Authorization: `Bearer ${token}` } })
                      .then(res => setWarRoomHQ(res.data))
                      .catch((e) => console.error("Export failed:", e));
                  }
                }}
                data-testid="summary-report-btn"
              >
                <FileText size={14} className="mr-1.5" />
                Summary Report
              </Button>

              {/* Date Range Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-auto lg:w-48 justify-start bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-[hsl(var(--text-primary))] hover:bg-[hsl(var(--surface-overlay))] text-xs lg:text-sm" data-testid="date-range-picker">
                    <CalendarIcon size={14} className="mr-1 lg:mr-2" />
                    <span className="truncate">
                    {dateRange === 'today' && 'Today'}
                    {dateRange === 'yesterday' && 'Yesterday'}
                    {dateRange === 'this-week' && 'This Week'}
                    {dateRange === 'last-week' && 'Last Week'}
                    {dateRange === 'this-month' && 'This Month'}
                    {dateRange === 'last-month' && 'Last Month'}
                    {dateRange === 'mtd' && `MTD: ${format(dateFrom, 'dd/MM')} - ${format(dateTo, 'dd/MM')}`}
                    {dateRange === 'full-2025' && 'Full Year 2025'}
                    {dateRange === 'full-2026' && 'Full Year 2026'}
                    {dateRange === 'full' && 'All Time'}
                    {dateRange === 'custom' && (dateFrom && dateTo ? `${format(dateFrom, 'dd/MM')} - ${format(dateTo, 'dd/MM')}` : 'Custom Range')}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[hsl(var(--card))] border-[hsl(var(--border))]" align="end">
                  <div className="p-3 border-b border-[hsl(var(--border))]">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">Quick Select</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { key: 'today', label: 'Today' },
                        { key: 'yesterday', label: 'Yesterday' },
                        { key: 'this-week', label: 'This Week' },
                        { key: 'last-week', label: 'Last Week' },
                        { key: 'this-month', label: 'This Month' },
                        { key: 'last-month', label: 'Last Month' },
                      ].map((range) => (
                        <Button key={range.key} size="sm" variant="ghost" onClick={() => setDateRange(range.key)} className={`text-white hover:bg-[hsl(var(--surface-elevated))] justify-start ${dateRange === range.key ? 'bg-[#E63946]/20 text-[#E63946]' : ''}`}>
                          {range.label}
                        </Button>
                      ))}
                      <Button size="sm" variant="ghost" onClick={() => setDateRange('full-2025')} className={`text-white hover:bg-[hsl(var(--surface-elevated))] justify-start ${dateRange === 'full-2025' ? 'bg-[#E63946]/20 text-[#E63946]' : ''}`}>
                        Full Year 2025
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDateRange('full-2026')} className={`text-white hover:bg-[hsl(var(--surface-elevated))] justify-start ${dateRange === 'full-2026' ? 'bg-[#E63946]/20 text-[#E63946]' : ''}`}>
                        Full Year 2026
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDateRange('full')} className={`text-white hover:bg-[hsl(var(--surface-elevated))] justify-start col-span-2 ${dateRange === 'full' ? 'bg-[#E63946]/20 text-[#E63946]' : ''}`}>
                        All Time
                      </Button>
                    </div>
                  </div>
                  <div className="p-4 border-t border-[hsl(var(--border))]">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#9CA3AF] mb-3">Custom Range</p>
                    <Popover open={showCustomDatePicker} onOpenChange={setShowCustomDatePicker}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={`w-full justify-start border-[hsl(var(--border))] text-white ${dateRange === 'custom' ? 'bg-[#E63946]/20 border-[#E63946]' : ''}`}>
                          <CalendarIcon size={14} className="mr-2" />
                          {dateRange === 'custom' && dateFrom && dateTo ? `${format(dateFrom, 'dd MMM yyyy')} - ${format(dateTo, 'dd MMM yyyy')}` : 'Pick custom dates'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" align="center" side="left">
                        <Calendar mode="range" selected={{ from: customDateFrom, to: customDateTo }} onSelect={(range) => {
                          if (range?.from) setCustomDateFrom(range.from);
                          if (range?.to) { setCustomDateTo(range.to); setDateRange('custom'); setShowCustomDatePicker(false); }
                        }} numberOfMonths={2} className="rounded-md" />
                      </PopoverContent>
                    </Popover>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-4 lg:px-8 py-4 lg:py-6 space-y-4 lg:space-y-6">
            
            {/* ONBOARDING CHECKLIST */}
            <OnboardingChecklist />
            
            {/* PERFORMANCE PROJECTIONS */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-[#8B5CF6]/[0.12] via-[#6B7280]/[0.08] to-[#8B5CF6]/[0.10] border border-[#8B5CF6]/30 rounded-xl overflow-hidden shadow-[0_0_30px_-10px_#8B5CF6]">
                <SectionHeader icon={TrendingUp} title="Performance Projections" subtitle="End of Month Forecast • All IBs" />
                
                <div className="p-6">
                  <div className="flex flex-wrap items-center gap-4 mb-6">
                    <Select value={scenario} onValueChange={setScenario}>
                      <SelectTrigger className="w-48 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                        <SelectItem value="base" className="text-white">Base Scenario</SelectItem>
                        <SelectItem value="lpv" className="text-white">LPV Conversion</SelectItem>
                        <SelectItem value="lead" className="text-white">Lead Conversion</SelectItem>
                        <SelectItem value="aggressive" className="text-white">Aggressive Growth</SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => setAdjustment(Math.max(-50, adjustment - 5))} className="border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--surface-elevated))]">-5%</Button>
                      <span className="text-sm font-mono text-white w-16 text-center bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded px-2 py-1">{adjustment > 0 ? '+' : ''}{adjustment}%</span>
                      <Button size="sm" variant="outline" onClick={() => setAdjustment(Math.min(50, adjustment + 5))} className="border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--surface-elevated))]">+5%</Button>
                    </div>

                    <Select value={forecastPeriod} onValueChange={setForecastPeriod}>
                      <SelectTrigger className="w-40 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                        <SelectItem value="month" className="text-white">End of Month</SelectItem>
                        <SelectItem value="q1" className="text-white">End of Q1</SelectItem>
                        <SelectItem value="year" className="text-white">End of Year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {projectionData.map((item) => {
                      const Icon = item.icon;
                      const projection = getProjection(item);
                      const gap = item.target - projection.projected;
                      const isOver = gap <= 0;
                      const perfTier = getPerformanceTier(projection.projectedPercent);
                      
                      return (
                        <div key={item.key} className="relative rounded-xl p-4 h-full border" style={{ backgroundColor: perfTier.bgColor, borderColor: perfTier.borderColor }}>
                          <div className="flex justify-center mb-2"><Icon size={18} style={{ color: item.iconColor }} /></div>
                          <p className="text-xs font-medium text-[#9CA3AF] mb-3 text-center">Projected {item.label}</p>
                          <p className="text-2xl font-mono font-bold text-white leading-none mb-3 text-center">{item.prefix || ''}{Math.round(projection.projected).toLocaleString()}</p>
                          <div className="flex flex-col items-center mb-3">
                            <p className="text-base font-mono font-bold" style={{ color: perfTier.color }}>{projection.projectedPercent}%</p>
                            <p className="text-[10px] font-medium" style={{ color: perfTier.color }}>{isOver ? `surplus : ${Math.round(Math.abs(gap)).toLocaleString()}` : `shortfall : ${Math.round(gap).toLocaleString()}`}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
                            <div className="text-center"><p className="text-[9px] uppercase tracking-wider text-[#6B7280]">Current</p><p className="text-xs font-mono font-semibold text-white">{item.prefix || ''}{item.current.toLocaleString()}</p></div>
                            <div className="text-center"><p className="text-[9px] uppercase tracking-wider text-[#6B7280]">Target</p><p className="text-xs font-mono font-semibold text-[#9CA3AF]">{item.prefix || ''}{item.target.toLocaleString()}</p></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* OVERALL PERFORMANCE SCORECARD */}
            <div className="relative group/section">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader icon={BarChart3} title="Overall Performance" subtitle="Key Metrics Scorecard • All IBs" />
                <div className="p-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {performanceMetrics.map((metric, idx) => {
                      const Icon = metric.icon;
                      const isPositive = metric.trend >= 0;
                      const hasData = metric.hasData !== false;
                      return (
                        <div key={idx} className={`relative p-4 rounded-xl border transition-all hover:scale-[1.02] ${metric.isRoi ? `border-2` : 'border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]'}`} style={metric.isRoi ? { borderColor: metric.roiTier?.color, backgroundColor: metric.roiTier?.bg } : {}}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">{metric.label}</span>
                            <Icon size={14} style={{ color: metric.color }} />
                          </div>
                          {hasData ? (
                            <>
                              <p className="text-xl font-mono font-bold text-white mb-1">{metric.isRoi ? `${metric.display}x` : metric.display}</p>
                              <div className="flex items-center gap-1">
                                {isPositive ? <ChevronUp size={14} className="text-[#10B981]" /> : <ChevronDown size={14} className="text-[#E63946]" />}
                                <span className={`text-xs font-medium ${isPositive ? 'text-[#10B981]' : 'text-[#E63946]'}`}>{Math.abs(metric.trend).toFixed(1)}%</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="text-xl font-mono font-bold text-[#4B5563] mb-1">-</p>
                              <p className="text-[10px] text-[#6B7280]">No data available</p>
                            </>
                          )}
                          {metric.isRoi && metric.roiTier && <Badge className="absolute top-2 right-2 text-[8px] px-1.5" style={{ backgroundColor: metric.roiTier.color, color: '#fff' }}>{metric.roiTier.tier}</Badge>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* CONTRIBUTIONS BY IB (renamed from Performance by IB) */}
            <div className="relative group/section">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#3B82F6]/10 via-transparent to-[#10B981]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader icon={PieChart} title="Contributions by IB" subtitle="Top 5 Introducing Brokers by Performance" />
                <div className="p-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {ibCategories.map((category) => {
                      const displayData = category.top5;
                      const total = category.full.reduce((sum, item) => sum + item.value, 0);
                      const hasMore = category.full.length > 5;
                      return (
                        <div key={category.key} className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                          <div className="flex items-center justify-between mb-4">
                            <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">{category.title}</p>
                            {hasMore && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => { setSelectedIbCategory(category); setIbContributionModalOpen(true); }} 
                                className="text-[#E63946] hover:bg-[#E63946]/10 text-[10px] h-6 px-2"
                                data-testid={`see-all-ib-${category.key}-btn`}
                              >
                                See All
                                <ChevronRight size={12} className="ml-1" />
                              </Button>
                            )}
                          </div>
                          <div className="h-32 relative">
                            <ResponsiveContainer width="100%" height="100%">
                              <RechartsPie>
                                <Pie data={displayData} cx="50%" cy="50%" innerRadius={30} outerRadius={50} paddingAngle={2} dataKey="value">
                                  {displayData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                              </RechartsPie>
                            </ResponsiveContainer>
                          </div>
                          <div className="mt-3 space-y-1.5">
                            {displayData.map((item, idx) => (
                              <div key={item.name} className="flex items-center text-xs">
                                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                  <span className="text-[10px] text-[#6B7280] w-3 flex-shrink-0">{idx + 1}.</span>
                                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                                  <span className="text-[#9CA3AF] truncate">{item.name}</span>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                  <span className="text-white font-mono font-semibold text-right w-20 text-[11px]">{category.prefix || ''}{item.value.toLocaleString()}</span>
                                  <span className="text-[#6B7280] text-[10px] w-8 text-right">({total > 0 ? ((item.value / total) * 100).toFixed(0) : 0}%)</span>
                                </div>
                              </div>
                            ))}
                            {hasMore && (
                              <div className="text-[10px] text-[#6B7280] text-center pt-2 border-t border-[hsl(var(--border))]">
                                +{category.full.length - 5} more IBs
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* PERFORMANCE BY BROKERS - FIXED: removed labels from pie */}
            <div className="relative group/section">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#F59E0B]/10 via-transparent to-[#8B5CF6]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader icon={Building2} title="Performance by Brokers" subtitle={`Total performance across ${brokerWithColors.length} broker partners`} />
                <div className="p-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {brokerCategories.map((category) => {
                      const total = category.data.reduce((sum, item) => sum + item.value, 0);
                      return (
                        <div key={category.key} className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                          <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-4 text-center">{category.title}</p>
                          <div className="h-32 relative">
                            <ResponsiveContainer width="100%" height="100%">
                              <RechartsPie>
                                <Pie data={category.data} cx="50%" cy="50%" innerRadius={30} outerRadius={50} paddingAngle={2} dataKey="value">
                                  {category.data.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                              </RechartsPie>
                            </ResponsiveContainer>
                          </div>
                          <div className="mt-3 space-y-1">
                            {category.data.map((item) => {
                              const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                              return (
                              <div key={item.name} className="flex items-center justify-between text-[10px]">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                                  <span className="text-[#9CA3AF]">{item.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-mono font-semibold">{category.prefix || ''}{item.value.toLocaleString()}</span>
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ color: item.color, backgroundColor: `${item.color}15` }}>{pct}%</span>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* TREND ANALYSIS 1: Spend > Closed */}
            <div className="relative group/section">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#10B981]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader icon={TrendingUp} title="Trend Analysis : Marketing Funnel" subtitle="Spend → Subscribers → Leads → Closed" />
                <div className="p-6">
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={trendDataMarketing} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} />
                      <YAxis yAxisId="left" stroke="#E63946" fontSize={11} tickLine={false} tickFormatter={(v) => `RM${(v/1000).toFixed(0)}k`} />
                      <YAxis yAxisId="right" orientation="right" stroke="#10B981" fontSize={11} tickLine={false} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: 'hsl(var(--surface-elevated))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                        formatter={(value, name) => [name === 'spend' ? `RM${value.toLocaleString()}` : value.toLocaleString(), name.charAt(0).toUpperCase() + name.slice(1)]}
                      />
                      <Legend wrapperStyle={{ paddingTop: '16px' }} formatter={(value) => <span className="text-[#9CA3AF] text-xs">{value.charAt(0).toUpperCase() + value.slice(1)}</span>} />
                      <Line yAxisId="left" type="monotone" dataKey="spend" stroke="#E63946" strokeWidth={3} dot={false} name="spend" />
                      <Line yAxisId="right" type="monotone" dataKey="subscribers" stroke="#3B82F6" strokeWidth={2} dot={false} name="subscribers" />
                      <Line yAxisId="right" type="monotone" dataKey="leads" stroke="#F97316" strokeWidth={2} dot={false} name="leads" />
                      <Line yAxisId="right" type="monotone" dataKey="closed" stroke="#10B981" strokeWidth={2} dot={false} name="closed" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* TREND ANALYSIS 2: Trading Performance */}
            <div className="relative group/section">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#3B82F6]/10 via-transparent to-[#8B5CF6]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader icon={BarChart3} title="Trend Analysis : Trading Performance" subtitle="Active Client → Traded Lot → Commissions" />
                <div className="p-6">
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={trendDataTrading} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <defs>
                        <linearGradient id="colorActiveClient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorTradedLot" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorCommissions" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} />
                      <YAxis yAxisId="left" stroke="#3B82F6" fontSize={11} tickLine={false} />
                      <YAxis yAxisId="right" orientation="right" stroke="#8B5CF6" fontSize={11} tickLine={false} tickFormatter={(v) => `RM${(v/1000).toFixed(0)}k`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: 'hsl(var(--surface-elevated))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                        formatter={(value, name) => {
                          if (name === 'commissions') return [`RM${value.toLocaleString()}`, 'Commissions'];
                          if (name === 'tradedLot') return [value.toFixed(1), 'Traded Lot'];
                          return [value, 'Active Client'];
                        }}
                      />
                      <Legend wrapperStyle={{ paddingTop: '16px' }} formatter={(value) => {
                        const labels = { activeClient: 'Active Client', tradedLot: 'Traded Lot', commissions: 'Commissions' };
                        return <span className="text-[#9CA3AF] text-xs">{labels[value] || value}</span>;
                      }} />
                      <Area yAxisId="left" type="monotone" dataKey="activeClient" stroke="#3B82F6" strokeWidth={2} fill="url(#colorActiveClient)" name="activeClient" />
                      <Area yAxisId="left" type="monotone" dataKey="tradedLot" stroke="#10B981" strokeWidth={2} fill="url(#colorTradedLot)" name="tradedLot" />
                      <Area yAxisId="right" type="monotone" dataKey="commissions" stroke="#8B5CF6" strokeWidth={2} fill="url(#colorCommissions)" name="commissions" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* TOP RANKINGS - 2 per row instead of 4 */}
            <div className="relative group/section">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#F59E0B]/10 via-transparent to-[#8B5CF6]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader icon={Crown} title="Top Rankings" subtitle="Best performing IBs by category" />
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'commission', title: 'Top by Commission', data: topByCommission, dataKey: 'commission', prefix: 'RM ' },
                      { key: 'lot', title: 'Top by Traded Lot', data: topByLot, dataKey: 'traded_lot', prefix: '' },
                      { key: 'deposit', title: 'Top by Deposit', data: topByDeposit, dataKey: 'deposit', prefix: '$' },
                      { key: 'client', title: 'Top by Active Client', data: topByClient, dataKey: 'active_client', prefix: '' },
                    ].map((category) => {
                      const rankIcons = [Crown, Medal, Trophy];
                      const rankColors = ['#F59E0B', '#9CA3AF', '#CD7F32'];
                      const rankBgColors = ['rgba(245, 158, 11, 0.15)', 'rgba(156, 163, 175, 0.10)', 'rgba(205, 127, 50, 0.10)'];
                      const displayData = category.data.slice(0, 5);
                      
                      return (
                        <div key={category.key} className="p-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                          <div className="flex items-center justify-between mb-4">
                            <p className="text-sm font-semibold text-white">{category.title}</p>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => { setSelectedRankingCategory(category); setRankingModalOpen(true); }} 
                              className="text-[#E63946] hover:bg-[#E63946]/10 text-xs"
                              data-testid={`see-all-${category.key}-btn`}
                            >
                              See All ({category.data.length})
                              <ChevronRight size={14} className="ml-1" />
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {displayData.map((ib, idx) => {
                              const RankIcon = rankIcons[idx] || Award;
                              const rankColor = rankColors[idx] || '#6B7280';
                              const rankBg = idx < 3 ? rankBgColors[idx] : 'transparent';
                              const ibColor = IB_COLORS[ib.ib_name] || '#6B7280';
                              const isTopThree = idx < 3;
                              
                              return (
                                <div 
                                  key={ib.ib_id} 
                                  className={`flex items-center gap-3 p-3 rounded-lg transition-all ${isTopThree ? 'border' : 'bg-[hsl(var(--surface))] hover:bg-[hsl(var(--surface))]/80'}`}
                                  style={{ 
                                    backgroundColor: rankBg,
                                    borderColor: isTopThree ? `${rankColor}40` : 'transparent'
                                  }}
                                >
                                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${rankColor}30` }}>
                                    {idx < 3 ? <RankIcon size={18} style={{ color: rankColor }} /> : <span className="text-sm font-bold text-[#6B7280]">{idx + 1}</span>}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium ${isTopThree ? 'text-white' : 'text-[#9CA3AF]'}`}>{ib.ib_name}</p>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ibColor }} />
                                      <span className="text-[10px] text-[#6B7280]">Rank #{idx + 1}</span>
                                    </div>
                                  </div>
                                  <p className={`text-base font-mono font-bold ${isTopThree ? 'text-white' : 'text-[#9CA3AF]'}`}>{category.prefix}{ib[category.dataKey]?.toLocaleString()}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* PERFORMANCE TRACKER - NEW SECTION */}
            <div className="relative group/section">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader icon={Target} title="Performance Tracker" subtitle={`Combined Monthly Progress | Day ${currentDay} of ${daysInMonth} | ${daysRemaining} days remaining`} />
                <div className="p-6">
                  <div className="overflow-x-auto pb-2">
                    <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
                      {trackerMetrics.map((metric) => {
                        const Icon = metric.icon;
                        const trackerCalc = calculateTrackerMetrics(metric, currentDay, daysInMonth, daysRemaining);
                        const progressColor = getProgressColor(trackerCalc.percentComplete);
                        const hasSurplus = trackerCalc.surplus > 0;
                        
                        return (
                          <div key={metric.key} className="w-[260px] flex-shrink-0 rounded-xl p-4 border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${metric.color}20` }}>
                                  <Icon size={14} style={{ color: metric.color }} />
                                </div>
                                <span className="text-xs font-medium text-[#9CA3AF]">{metric.label}</span>
                              </div>
                              <Button size="sm" variant="outline" className="border-[#A855F7]/40 text-[#A855F7] hover:bg-[#A855F7]/20 hover:text-[#C084FC] hover:border-[#A855F7] text-[10px] px-2 py-1 h-6" onClick={() => { setSelectedMetric(metric); setInsightModalOpen(true); }}>
                                <Eye size={10} className="mr-1" />Insight
                              </Button>
                            </div>
                            
                            {/* Current Score */}
                            <p className="text-2xl font-mono font-bold text-white mb-1">{metric.prefix || ''}{metric.current.toLocaleString()}{metric.suffix || ''}</p>
                            <p className="text-xs text-[#6B7280] mb-3">Target: <span className="font-mono font-semibold text-[#9CA3AF]">{metric.prefix || ''}{metric.target.toLocaleString()}{metric.suffix || ''}</span></p>
                            
                            {/* Progress Bar */}
                            <div className="mb-3">
                              <div className="flex justify-between text-[10px] mb-1">
                                <span style={{ color: progressColor }} className="font-semibold">{trackerCalc.percentComplete}%</span>
                                <span className="text-[#6B7280]">{trackerCalc.shortfall > 0 ? `${trackerCalc.shortfall.toLocaleString()} left` : 'Complete'}</span>
                              </div>
                              <div className="h-2 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(trackerCalc.percentComplete, 100)}%`, backgroundColor: progressColor }} />
                              </div>
                            </div>
                            
                            {/* Stats Row */}
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <div className="bg-[hsl(var(--surface))] rounded p-2">
                                <p className="text-[9px] uppercase tracking-wider text-[#6B7280] mb-0.5">{hasSurplus ? 'Surplus' : 'Shortfall'}</p>
                                <p className="text-sm font-mono font-bold truncate" style={{ color: hasSurplus ? '#10B981' : '#E63946' }}>
                                  {hasSurplus ? `+${metric.prefix || ''}${trackerCalc.surplus.toLocaleString()}` : `${metric.prefix || ''}${trackerCalc.shortfall.toLocaleString()}`}
                                </p>
                              </div>
                              <div className="bg-[hsl(var(--surface))] rounded p-2">
                                <p className="text-[9px] uppercase tracking-wider text-[#6B7280] mb-0.5">Next Day</p>
                                <p className="text-sm font-mono font-bold text-[#F59E0B] truncate">{metric.prefix || ''}{trackerCalc.nextDayTarget.toLocaleString()}</p>
                              </div>
                            </div>
                            
                            {/* Daily & Schedule */}
                            <div className="flex items-center justify-between text-[10px] pt-2 border-t border-[hsl(var(--border))]">
                              <span className="text-[#6B7280]">Daily: <span className="font-mono text-white">{metric.prefix || ''}{metric.dailyTarget.toLocaleString()}</span></span>
                              {trackerCalc.daysBehind > 0 && (
                                <span className="flex items-center gap-1 text-[#E63946]">
                                  <AlertTriangle size={10} />
                                  {trackerCalc.daysBehind}d behind
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

      {/* INSIGHT MODAL */}
      <Dialog open={insightModalOpen} onOpenChange={setInsightModalOpen}>
        <DialogContent className="sm:max-w-3xl bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              {selectedMetric && (
                <>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${selectedMetric.color}20` }}>
                    {React.createElement(selectedMetric.icon, { size: 20, style: { color: selectedMetric.color } })}
                  </div>
                  {selectedMetric.label} Insight
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedMetric && (
            <div className="space-y-6 mt-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Current</p>
                  <p className="text-xl font-mono font-bold text-white">{selectedMetric.prefix || ''}{selectedMetric.current.toLocaleString()}</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Target</p>
                  <p className="text-xl font-mono font-bold text-[#9CA3AF]">{selectedMetric.prefix || ''}{selectedMetric.target.toLocaleString()}</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Shortfall</p>
                  <p className="text-xl font-mono font-bold text-[#E63946]">{selectedMetric.prefix || ''}{Math.max(0, selectedMetric.target - selectedMetric.current).toLocaleString()}</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">% Complete</p>
                  <p className="text-xl font-mono font-bold" style={{ color: getProgressColor(((selectedMetric.current / selectedMetric.target) * 100)) }}>{((selectedMetric.current / selectedMetric.target) * 100).toFixed(1)}%</p>
                </div>
              </div>

              {/* Trend Chart */}
              <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4">
                <p className="text-sm font-semibold text-white mb-4">Monthly Performance Trend</p>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={generateDailyData(selectedMetric, currentDay, daysInMonth, dateFrom.getMonth() + 1)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="#6B7280" fontSize={10} />
                    <YAxis stroke="#6B7280" fontSize={10} />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: '#fff' }} />
                    <Legend />
                    <Area type="monotone" dataKey="actual" name="Actual (Cumulative)" stroke={selectedMetric.color} fill={`${selectedMetric.color}30`} strokeWidth={2} dot={false} />
                    <Area type="monotone" dataKey="target" name="Target (Cumulative)" stroke="#6B7280" fill="transparent" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Daily Values Table */}
              <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4">
                <p className="text-sm font-semibold text-white mb-4">Daily Values</p>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[hsl(var(--surface-elevated))]">
                      <tr className="border-b border-[hsl(var(--border))]">
                        <th className="text-left py-2 text-[#6B7280]">Day</th>
                        <th className="text-right py-2 text-[#6B7280]">Actual</th>
                        <th className="text-right py-2 text-[#6B7280]">Target</th>
                        <th className="text-right py-2 text-[#6B7280]">Variance</th>
                        <th className="text-right py-2 text-[#6B7280]">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generateDailyData(selectedMetric, currentDay, daysInMonth, dateFrom.getMonth() + 1).filter(d => d.dailyActual !== null).slice(-10).reverse().map((day) => (
                        <tr key={day.day} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--surface))]">
                          <td className="py-2 text-white">{day.date}</td>
                          <td className="py-2 text-right text-white font-mono">{selectedMetric.prefix || ''}{day.dailyActual?.toLocaleString()}</td>
                          <td className="py-2 text-right text-[#9CA3AF] font-mono">{selectedMetric.prefix || ''}{day.dailyTarget?.toLocaleString()}</td>
                          <td className="py-2 text-right font-mono" style={{ color: (day.dailyActual - day.dailyTarget) >= 0 ? '#10B981' : '#E63946' }}>
                            {(day.dailyActual - day.dailyTarget) >= 0 ? '+' : ''}{selectedMetric.prefix || ''}{(day.dailyActual - day.dailyTarget)?.toLocaleString()}
                          </td>
                          <td className="py-2 text-right font-mono" style={{ color: parseFloat(day.variancePercent) >= 0 ? '#10B981' : '#E63946' }}>
                            {parseFloat(day.variancePercent) >= 0 ? '+' : ''}{day.variancePercent}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RANKING MODAL - See All IBs */}
      <Dialog open={rankingModalOpen} onOpenChange={setRankingModalOpen}>
        <DialogContent className="sm:max-w-xl bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-h-[85vh] overflow-y-auto" data-testid="ranking-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              {selectedRankingCategory && (
                <>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#F59E0B]/30 to-[#F97316]/20 border border-[#F59E0B]/40">
                    <Crown size={20} className="text-[#F59E0B]" />
                  </div>
                  {selectedRankingCategory.title}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRankingCategory && (
            <div className="space-y-3 mt-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Total IBs</p>
                  <p className="text-xl font-bold text-white">{selectedRankingCategory.data.length}</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Top Performer</p>
                  <p className="text-sm font-bold text-[#F59E0B] truncate">{selectedRankingCategory.data[0]?.ib_name || '-'}</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Total</p>
                  <p className="text-sm font-bold text-white">{selectedRankingCategory.prefix}{selectedRankingCategory.data.reduce((sum, ib) => sum + (ib[selectedRankingCategory.dataKey] || 0), 0).toLocaleString()}</p>
                </div>
              </div>

              {/* Full IB List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {selectedRankingCategory.data.map((ib, idx) => {
                  const rankIcons = [Crown, Medal, Trophy];
                  const rankColors = ['#F59E0B', '#9CA3AF', '#CD7F32'];
                  const RankIcon = rankIcons[idx] || Award;
                  const rankColor = rankColors[idx] || '#6B7280';
                  const ibColor = IB_COLORS[ib.ib_name] || '#6B7280';
                  const topValue = selectedRankingCategory.data[0]?.[selectedRankingCategory.dataKey] || 1;
                  const percentage = ((ib[selectedRankingCategory.dataKey] || 0) / topValue * 100).toFixed(1);
                  
                  return (
                    <div key={ib.ib_id} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--surface-elevated))] hover:bg-[hsl(var(--surface-elevated))]/80 transition-all border border-[hsl(var(--border))]" data-testid={`ranking-item-${idx}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${rankColor}20` }}>
                        {idx < 3 ? <RankIcon size={20} style={{ color: rankColor }} /> : <span className="text-lg font-bold text-[#6B7280]">{idx + 1}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-white">{ib.ib_name}</p>
                          {idx === 0 && <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] text-[8px] px-1.5">TOP</Badge>}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${percentage}%`, backgroundColor: ibColor }} />
                          </div>
                          <span className="text-[10px] text-[#6B7280] w-12 text-right">{percentage}%</span>
                        </div>
                      </div>
                      <p className="text-lg font-mono font-bold text-white">{selectedRankingCategory.prefix}{ib[selectedRankingCategory.dataKey]?.toLocaleString()}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* IB CONTRIBUTION MODAL - See All IBs */}
      <Dialog open={ibContributionModalOpen} onOpenChange={setIbContributionModalOpen}>
        <DialogContent className="sm:max-w-xl bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-h-[85vh] overflow-y-auto" data-testid="ib-contribution-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              {selectedIbCategory && (
                <>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#3B82F6]/30 to-[#10B981]/20 border border-[#3B82F6]/40">
                    <PieChart size={20} className="text-[#3B82F6]" />
                  </div>
                  {selectedIbCategory.title} - All IBs
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedIbCategory && (
            <div className="space-y-3 mt-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Total IBs</p>
                  <p className="text-xl font-bold text-white">{selectedIbCategory.full.length}</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Top Contributor</p>
                  <p className="text-sm font-bold text-[#3B82F6] truncate">{selectedIbCategory.full[0]?.name || '-'}</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Total</p>
                  <p className="text-sm font-bold text-white">{selectedIbCategory.prefix || ''}{selectedIbCategory.full.reduce((sum, item) => sum + (item.value || 0), 0).toLocaleString()}</p>
                </div>
              </div>

              {/* Full IB List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {selectedIbCategory.full.map((item, idx) => {
                  const rankIcons = [Crown, Medal, Trophy];
                  const rankColors = ['#F59E0B', '#9CA3AF', '#CD7F32'];
                  const RankIcon = rankIcons[idx] || Award;
                  const rankColor = rankColors[idx] || '#6B7280';
                  const topValue = selectedIbCategory.full[0]?.value || 1;
                  const percentage = ((item.value || 0) / topValue * 100).toFixed(1);
                  const total = selectedIbCategory.full.reduce((sum, i) => sum + (i.value || 0), 0);
                  const sharePercent = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0';
                  
                  return (
                    <div key={item.name} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--surface-elevated))] hover:bg-[hsl(var(--surface-elevated))]/80 transition-all border border-[hsl(var(--border))]" data-testid={`ib-contribution-item-${idx}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${rankColor}20` }}>
                        {idx < 3 ? <RankIcon size={20} style={{ color: rankColor }} /> : <span className="text-lg font-bold text-[#6B7280]">{idx + 1}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <p className="text-sm font-semibold text-white">{item.name}</p>
                          {idx === 0 && <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] text-[8px] px-1.5">TOP</Badge>}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${percentage}%`, backgroundColor: item.color }} />
                          </div>
                          <span className="text-[10px] text-[#6B7280] w-12 text-right">{sharePercent}%</span>
                        </div>
                      </div>
                      <p className="text-lg font-mono font-bold text-white">{selectedIbCategory.prefix || ''}{item.value?.toLocaleString()}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== SUMMARY REPORT MODAL ==================== */}
      <Dialog open={summaryModalOpen} onOpenChange={setSummaryModalOpen}>
        <DialogContent className="sm:max-w-2xl bg-[#0a0a0f] border-[#1a1a25] text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5 text-base font-bold tracking-tight">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E63946] to-[#c62828] flex items-center justify-center">
                <FileText size={15} className="text-white" />
              </div>
              Performance Summary
            </DialogTitle>
          </DialogHeader>
          
          {/* Period Selector */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {['weekly', 'monthly'].map(t => (
              <button key={t} onClick={() => { setSummaryType(t); setSummaryWeek(0); }}
                className={`px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${summaryType === t ? 'bg-[#E63946] text-white shadow-lg shadow-[#E63946]/20' : 'bg-[#111118] text-[#6b7280] border border-[#1a1a25] hover:text-white'}`}
                data-testid={`summary-${t}-btn`}
              >{t}</button>
            ))}
            {summaryType === 'weekly' && (
              <div className="flex items-center gap-1 ml-2">
                {[1,2,3,4].map(w => (
                  <button key={w} onClick={() => setSummaryWeek(w)}
                    className={`px-2.5 py-1.5 rounded text-[9px] font-bold transition-all ${summaryWeek === w ? 'bg-[#E63946]/20 text-[#E63946] border border-[#E63946]/40' : 'bg-[#111118] text-[#6b7280] border border-[#1a1a25] hover:text-white'}`}
                  >W{w}</button>
                ))}
                <button onClick={() => setSummaryWeek(0)}
                  className={`px-2.5 py-1.5 rounded text-[9px] font-bold transition-all ${summaryWeek === 0 ? 'bg-[#E63946]/20 text-[#E63946] border border-[#E63946]/40' : 'bg-[#111118] text-[#6b7280] border border-[#1a1a25] hover:text-white'}`}
                >MTD</button>
              </div>
            )}
          </div>

          {(() => {
            // Filter data by selected week if applicable
            const dailyTrends = data?.daily_trends || [];
            let filteredTrends = dailyTrends;
            let weekStart = null, weekEnd = null;
            
            if (summaryType === 'weekly' && summaryWeek > 0 && dailyTrends.length > 0) {
              const month = dateTo.getMonth();
              const year = dateTo.getFullYear();
              const daysInMonth = new Date(year, month + 1, 0).getDate();
              weekStart = new Date(year, month, (summaryWeek - 1) * 7 + 1);
              weekEnd = new Date(year, month, Math.min(summaryWeek * 7, daysInMonth));
              const ws = format(weekStart, 'yyyy-MM-dd');
              const we = format(weekEnd, 'yyyy-MM-dd');
              filteredTrends = dailyTrends.filter(d => d.date >= ws && d.date <= we);
            }
            
            // Recalculate metrics from filtered daily trends
            const useFiltered = summaryType === 'weekly' && summaryWeek > 0 && filteredTrends.length > 0;
            const g = useFiltered ? {
              total_commission: filteredTrends.reduce((s, d) => s + (d.commissions || 0), 0),
              total_leads: filteredTrends.reduce((s, d) => s + (d.leads || 0), 0),
              total_closed: filteredTrends.reduce((s, d) => s + (d.closed || 0), 0),
              total_spend: filteredTrends.reduce((s, d) => s + (d.spend || 0), 0),
              total_deposit: (data?.global_metrics?.total_deposit || 0),
              total_active_client: filteredTrends.reduce((s, d) => s + (d.active_client || 0), 0),
              total_traded_lot: filteredTrends.reduce((s, d) => s + (d.traded_lot || 0), 0),
              total_subscribers: filteredTrends.reduce((s, d) => s + (d.subscribers || 0), 0),
            } : (data?.global_metrics || {});
            const pg = prevData?.global_metrics || {};
            const ibPerf = data?.ib_performance || [];
            const brokers = data?.broker_performance || [];
            const pct = (c, p) => p > 0 ? (((c - p) / p) * 100).toFixed(1) : (c > 0 ? '+100' : '0');
            const arrow = (c, p) => c > p ? '▲' : c < p ? '▼' : '—';
            const periodLabel = summaryType === 'weekly'
              ? (summaryWeek > 0 ? `Week ${summaryWeek} (${weekStart ? format(weekStart, 'dd/MM') : ''} - ${weekEnd ? format(weekEnd, 'dd/MM') : ''}) — ${format(dateTo, 'MMMM yyyy')}` : `MTD — ${format(dateTo, 'MMMM yyyy')}`)
              : format(dateTo, 'MMMM yyyy');
            
            // Sort IBs by commission
            const sortedIBs = [...ibPerf].sort((a, b) => (b.commission || 0) - (a.commission || 0));
            const top3 = sortedIBs.slice(0, 3);
            const bottom3 = sortedIBs.filter(ib => ib.commission > 0).slice(-3).reverse();
            
            // Find most improved
            const prevIbPerf = prevData?.ib_performance || [];
            const ibChanges = sortedIBs.map(ib => {
              const prev = prevIbPerf.find(p => p.ib_id === ib.ib_id);
              const commChange = prev ? parseFloat(pct(ib.commission || 0, prev.commission || 0)) : 0;
              return { ...ib, commChange };
            }).sort((a, b) => b.commChange - a.commChange);
            const mostImproved = ibChanges.filter(i => i.commChange > 0).slice(0, 2);
            const mostDropped = ibChanges.filter(i => i.commChange < -10).slice(-2);
            const closingRate = g.total_leads > 0 ? ((g.total_closed / g.total_leads) * 100).toFixed(1) : '0.0';
            const prevCR = pg.total_leads > 0 ? ((pg.total_closed / pg.total_leads) * 100).toFixed(1) : '0.0';

            return (
              <div id="summary-report-content" className="space-y-5">
                {/* Header */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-[#E63946]/20 to-transparent border border-[#E63946]/30">
                  <h3 className="text-base font-bold">{summaryType === 'weekly' ? 'Weekly' : 'Monthly'} Performance Report</h3>
                  <p className="text-[11px] text-[hsl(var(--text-muted))]">Underads Capital — {periodLabel}</p>
                </div>

                {/* Executive Summary */}
                <div>
                  <h4 className="text-[9px] font-bold uppercase tracking-widest text-[#6b7280] mb-2">Key Metrics</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {[
                      { label: 'Commission', val: `RM ${(g.total_commission || 0).toLocaleString('en-MY', {maximumFractionDigits:0})}`, prev: pg.total_commission, cur: g.total_commission, color: '#10b981' },
                      { label: 'Total Leads', val: (g.total_leads || 0).toLocaleString(), prev: pg.total_leads, cur: g.total_leads, color: '#3b82f6' },
                      { label: 'Closed', val: (g.total_closed || 0).toLocaleString(), prev: pg.total_closed, cur: g.total_closed, color: '#f59e0b' },
                      { label: 'Ad Spend', val: `RM ${(g.total_spend || 0).toLocaleString('en-MY', {maximumFractionDigits:0})}`, prev: pg.total_spend, cur: g.total_spend, color: '#ef4444' },
                      { label: 'ROI', val: g.total_spend > 0 ? `${((g.total_commission / g.total_spend) * 100).toFixed(0)}%` : '—', prev: pg.total_spend > 0 ? (pg.total_commission / pg.total_spend) * 100 : 0, cur: g.total_spend > 0 ? (g.total_commission / g.total_spend) * 100 : 0, color: '#8b5cf6' },
                    ].map(m => (
                      <div key={m.label} className="p-2.5 rounded-lg bg-[#111118] border border-[#1a1a25]">
                        <div className="text-[8px] font-bold uppercase tracking-widest text-[#6b7280]">{m.label}</div>
                        <div className="text-sm font-bold font-mono" style={{color: m.color}}>{m.val}</div>
                        <div className="text-[9px] font-bold" style={{color: (m.cur||0) >= (m.prev||0) ? '#10b981' : '#ef4444'}}>
                          {(m.cur||0) > (m.prev||0) ? '▲' : (m.cur||0) < (m.prev||0) ? '▼' : '—'} {pct(m.cur||0, m.prev||0)}%
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
                    {(() => {
                      const curCpl = (g.total_traded_lot || 0) > 0 ? ((g.total_commission_usd || (g.total_commission || 0) / 4.2) / g.total_traded_lot) : 0;
                      const prevCpl = (pg.total_traded_lot || 0) > 0 ? ((pg.total_commission_usd || (pg.total_commission || 0) / 4.2) / pg.total_traded_lot) : 0;
                      return [
                        { label: 'Deposit', val: `$ ${(g.total_deposit || 0).toLocaleString('en-MY', {maximumFractionDigits:0})}`, prev: pg.total_deposit, cur: g.total_deposit },
                        { label: 'Active Client', val: (g.total_active_client || 0).toLocaleString(), prev: pg.total_active_client, cur: g.total_active_client },
                        { label: 'Traded Lot', val: (g.total_traded_lot || 0).toLocaleString('en-MY', {maximumFractionDigits:1}), prev: pg.total_traded_lot, cur: g.total_traded_lot },
                        { label: 'Com/Lot', val: curCpl > 0 ? `$${curCpl.toFixed(2)}` : '—', prev: prevCpl, cur: curCpl, color: curCpl >= 12 ? '#10b981' : curCpl >= 8 ? '#f59e0b' : curCpl > 0 ? '#ef4444' : undefined },
                        { label: 'Closing Rate', val: closingRate + '%', prev: parseFloat(prevCR), cur: parseFloat(closingRate) },
                      ];
                    })().map(m => (
                      <div key={m.label} className="p-2.5 rounded-lg bg-[#111118] border border-[#1a1a25]">
                        <div className="text-[8px] font-bold uppercase tracking-widest text-[#6b7280]">{m.label}</div>
                        <div className="text-sm font-bold font-mono" style={{color: m.color || 'white'}}>{m.val}</div>
                        <div className="text-[9px] font-bold" style={{color: (m.cur||0) >= (m.prev||0) ? '#10b981' : '#ef4444'}}>
                          {(m.cur||0) > (m.prev||0) ? '▲' : (m.cur||0) < (m.prev||0) ? '▼' : '—'} {pct(m.cur||0, m.prev||0)}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Commission by Channel — Visual Bar Chart */}
                <div>
                  <h4 className="text-[9px] font-bold uppercase tracking-widest text-[#6b7280] mb-2">Commission by Channel</h4>
                  <div className="space-y-1.5">
                    {sortedIBs.filter(ib => (ib.commission||0) > 0).slice(0, 8).map((ib, i) => {
                      const maxComm = sortedIBs[0]?.commission || 1;
                      const width = ((ib.commission || 0) / maxComm * 100).toFixed(0);
                      const prevIb = prevIbPerf.find(p => p.ib_id === ib.ib_id);
                      const chg = prevIb ? parseFloat(pct(ib.commission||0, prevIb.commission||0)) : 0;
                      return (
                        <div key={ib.ib_id} className="flex items-center gap-2">
                          <span className="text-[9px] text-[#9ca3af] w-24 truncate text-right">{ib.ib_name}</span>
                          <div className="flex-1 h-5 bg-[#111118] rounded overflow-hidden relative">
                            <div className="h-full rounded transition-all" style={{width: `${width}%`, background: `linear-gradient(90deg, #E63946, ${i < 3 ? '#f97316' : '#6b7280'})`}} />
                            <span className="absolute inset-0 flex items-center px-2 text-[8px] font-bold text-white">
                              RM {(ib.commission||0).toLocaleString('en-MY',{maximumFractionDigits:0})}
                            </span>
                          </div>
                          <span className={`text-[9px] font-bold w-10 text-right ${chg >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                            {chg >= 0 ? '▲' : '▼'}{Math.abs(chg).toFixed(0)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Channel Rankings */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">Channel Rankings</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Top Performers */}
                    <div className="p-3 rounded-xl border border-[#10b981]/30 bg-[#10b981]/5">
                      <div className="text-[10px] font-bold text-[#10b981] uppercase mb-2">Top Performers</div>
                      {top3.map((ib, i) => (
                        <div key={ib.ib_id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--border))]/20 last:border-0">
                          <span className="text-[11px] text-white flex items-center gap-1.5">
                            <span className="w-5 h-5 rounded flex items-center justify-center text-[8px] font-bold" style={{
                            background: i === 0 ? '#E63946' : i === 1 ? '#6b7280' : '#92400e', color: 'white'
                          }}>{i+1}</span>
                          {ib.ib_name}
                          </span>
                          <span className="text-[11px] font-mono font-bold text-[#10b981]">RM{(ib.commission || 0).toLocaleString('en-MY', {maximumFractionDigits:0})}</span>
                        </div>
                      ))}
                    </div>
                    {/* Most Improved */}
                    <div className="p-3 rounded-xl border border-[#3b82f6]/30 bg-[#3b82f6]/5">
                      <div className="text-[10px] font-bold text-[#3b82f6] uppercase mb-2">Most Improved</div>
                      {mostImproved.length > 0 ? mostImproved.map(ib => (
                        <div key={ib.ib_id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--border))]/20 last:border-0">
                          <span className="text-[11px] text-white"><TrendingUp size={10} className="inline mr-1 text-[#3b82f6]" />{ib.ib_name}</span>
                          <span className="text-[11px] font-mono font-bold text-[#3b82f6]">+{ib.commChange.toFixed(0)}%</span>
                        </div>
                      )) : <p className="text-[10px] text-[hsl(var(--text-muted))]">Tiada data perbandingan</p>}
                    </div>
                    {/* Needs Attention */}
                    {mostDropped.length > 0 && (
                      <div className="p-3 rounded-xl border border-[#ef4444]/30 bg-[#ef4444]/5">
                        <div className="text-[10px] font-bold text-[#ef4444] uppercase mb-2">Needs Attention</div>
                        {mostDropped.map(ib => (
                          <div key={ib.ib_id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--border))]/20 last:border-0">
                            <span className="text-[11px] text-white"><TrendingDown size={10} className="inline mr-1 text-[#ef4444]" />{ib.ib_name}</span>
                            <span className="text-[11px] font-mono font-bold text-[#ef4444]">{ib.commChange.toFixed(0)}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Broker Performance */}
                    {brokers.length > 0 && (
                      <div className="p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                        <div className="text-[10px] font-bold text-[hsl(var(--text-muted))] uppercase mb-2">Broker Performance</div>
                        {brokers.slice(0, 4).map(b => (
                          <div key={b.broker_name} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--border))]/20 last:border-0">
                            <span className="text-[11px] text-white">{b.broker_name}</span>
                            <span className="text-[11px] font-mono text-[hsl(var(--text-muted))]">RM{(b.commission || 0).toLocaleString()} | {b.active_client} clients</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Marketing Efficiency + Funnel */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-[#111118] border border-[#1a1a25]">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-[#6b7280] mb-2">Marketing Efficiency</div>
                    {[
                      { label: 'CPS', val: g.total_subscribers > 0 ? `RM ${(g.total_spend / g.total_subscribers).toFixed(2)}` : '—', good: g.total_subscribers > 0 && (g.total_spend / g.total_subscribers) < 15 },
                      { label: 'CPL', val: g.total_leads > 0 ? `RM ${(g.total_spend / g.total_leads).toFixed(2)}` : '—', good: g.total_leads > 0 && (g.total_spend / g.total_leads) < 50 },
                      { label: 'Cost/Client', val: g.total_closed > 0 ? `RM ${(g.total_spend / g.total_closed).toFixed(0)}` : '—', good: g.total_closed > 0 && (g.total_spend / g.total_closed) < 500 },
                    ].map(m => (
                      <div key={m.label} className="flex items-center justify-between py-1.5 border-b border-[#1a1a25]/50 last:border-0">
                        <span className="text-[10px] text-[#9ca3af]">{m.label}</span>
                        <span className={`text-[10px] font-mono font-bold ${m.good ? 'text-[#10b981]' : 'text-[#f59e0b]'}`}>{m.val}</span>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 rounded-lg bg-[#111118] border border-[#1a1a25]">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-[#6b7280] mb-2">Sales Funnel</div>
                    {[
                      { label: 'Subscribers', val: g.total_subscribers || 0, pct: 100 },
                      { label: 'Leads', val: g.total_leads || 0, pct: g.total_subscribers > 0 ? ((g.total_leads / g.total_subscribers) * 100) : 0 },
                      { label: 'Closed', val: g.total_closed || 0, pct: g.total_leads > 0 ? ((g.total_closed / g.total_leads) * 100) : 0 },
                      { label: 'Active', val: g.total_active_client || 0, pct: g.total_closed > 0 ? ((g.total_active_client / g.total_closed) * 100) : 0 },
                    ].map((s, i) => (
                      <div key={s.label} className="mb-1.5 last:mb-0">
                        <div className="flex justify-between text-[9px] mb-0.5">
                          <span className="text-[#9ca3af]">{s.label}</span>
                          <span className="font-mono text-white">{s.val.toLocaleString()} {i > 0 ? `(${s.pct.toFixed(1)}%)` : ''}</span>
                        </div>
                        <div className="h-1.5 bg-[#1a1a25] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{width: `${Math.min(s.pct, 100)}%`, background: ['#3b82f6','#f59e0b','#10b981','#8b5cf6'][i]}} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ──────────── SECTION: Target Achievement ──────────── */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-px flex-1 bg-[#1a1a25]" />
                    <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#6b7280]">Target Achievement</span>
                    <div className="h-px flex-1 bg-[#1a1a25]" />
                  </div>
                  <p className="text-[9px] text-[#4b5563] mb-2">Monthly target progress — pace vs expected</p>
                  <div className="space-y-2">
                    {(() => {
                      const tgt = aggregatedTargets || {};
                      const daysPassed = dateTo.getDate();
                      const daysInMonth = new Date(dateTo.getFullYear(), dateTo.getMonth() + 1, 0).getDate();
                      const expectedPace = (daysPassed / daysInMonth) * 100;
                      return [
                        { label: 'Commission', actual: g.total_commission || 0, target: tgt.commission || 0, prefix: 'RM ' },
                        { label: 'Leads', actual: g.total_leads || 0, target: tgt.leads || 0, prefix: '' },
                        { label: 'Closed', actual: g.total_closed || 0, target: tgt.closed || 0, prefix: '' },
                        { label: 'Deposit', actual: g.total_deposit || 0, target: tgt.deposit || 0, prefix: '$ ' },
                        { label: 'Subscribers', actual: g.total_subscribers || 0, target: tgt.subscribers || 0, prefix: '' },
                      ].filter(m => m.target > 0).map(m => {
                        const achieved = m.target > 0 ? ((m.actual / m.target) * 100) : 0;
                        const onTrack = achieved >= expectedPace;
                        return (
                          <div key={m.label}>
                            <div className="flex justify-between mb-1">
                              <span className="text-[9px] text-[#9ca3af]">{m.label}</span>
                              <span className="text-[9px] font-mono">
                                <span className="text-white">{m.prefix}{m.actual.toLocaleString('en-MY', {maximumFractionDigits:0})}</span>
                                <span className="text-[#4b5563]"> / {m.prefix}{m.target.toLocaleString('en-MY', {maximumFractionDigits:0})}</span>
                                <span className={`ml-2 font-bold ${onTrack ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>{Math.min(achieved, 100).toFixed(0)}%</span>
                              </span>
                            </div>
                            <div className="h-2 bg-[#1a1a25] rounded-full overflow-hidden relative">
                              <div className="h-full rounded-full transition-all" style={{width: `${Math.min(achieved, 100)}%`, background: onTrack ? '#10b981' : '#ef4444'}} />
                              <div className="absolute top-0 h-full w-px bg-[#f59e0b]" style={{left: `${expectedPace}%`}} title={`Expected pace: ${expectedPace.toFixed(0)}%`} />
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* ──────────── SECTION: War Room Health ──────────── */}
                {warRoomHQ && warRoomHQ.channels && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-px flex-1 bg-[#1a1a25]" />
                      <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#6b7280]">Channel Health Status</span>
                      <div className="h-px flex-1 bg-[#1a1a25]" />
                    </div>
                    <p className="text-[9px] text-[#4b5563] mb-2">War Room diagnostics — real-time health assessment</p>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {warRoomHQ.channels.filter(c => c.has_data).sort((a,b) => a.health - b.health).map(ch => {
                        const color = ch.health >= 80 ? '#10b981' : ch.health >= 50 ? '#f59e0b' : '#ef4444';
                        const label = ch.health >= 80 ? 'Healthy' : ch.health >= 50 ? 'Attention' : 'Critical';
                        return (
                          <div key={ch.ib_id} className="p-2 rounded-lg bg-[#111118] border border-[#1a1a25] text-center">
                            <div className="text-[9px] text-[#9ca3af] truncate mb-1">{ch.name}</div>
                            <div className="text-lg font-bold font-mono" style={{color}}>{ch.health}</div>
                            <div className="text-[8px] font-bold uppercase" style={{color}}>{label}</div>
                            {ch.content_score > 0 && (
                              <div className="text-[8px] text-[#6b7280] mt-0.5">Content: {ch.content_score}/10</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ──────────── SECTION: Telegram Growth ──────────── */}
                {warRoomHQ && warRoomHQ.channels && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-px flex-1 bg-[#1a1a25]" />
                      <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#6b7280]">Telegram Channels</span>
                      <div className="h-px flex-1 bg-[#1a1a25]" />
                    </div>
                    <p className="text-[9px] text-[#4b5563] mb-2">Subscriber base and growth across all channels</p>
                    <div className="space-y-1.5">
                      {warRoomHQ.channels.filter(c => c.tg_subs > 0).sort((a,b) => b.tg_subs - a.tg_subs).map(ch => {
                        const maxSubs = warRoomHQ.channels.reduce((m, c) => Math.max(m, c.tg_subs || 0), 1);
                        return (
                          <div key={ch.ib_id} className="flex items-center gap-2">
                            <span className="text-[9px] text-[#9ca3af] w-20 truncate text-right">{ch.name}</span>
                            <div className="flex-1 h-4 bg-[#111118] rounded overflow-hidden relative">
                              <div className="h-full rounded" style={{width: `${(ch.tg_subs / maxSubs * 100)}%`, background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)'}} />
                              <span className="absolute inset-0 flex items-center px-2 text-[8px] font-bold text-white">
                                {ch.tg_subs.toLocaleString()}
                              </span>
                            </div>
                            {ch.content_score > 0 && (
                              <span className={`text-[8px] font-bold w-8 ${ch.content_score >= 7 ? 'text-[#10b981]' : ch.content_score >= 4 ? 'text-[#f59e0b]' : 'text-[#ef4444]'}`}>
                                {ch.content_score}/10
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ──────────── SECTION: Risk Alerts ──────────── */}
                {(() => {
                  const tgt = aggregatedTargets || {};
                  const daysPassed = dateTo.getDate();
                  const daysInMonth = new Date(dateTo.getFullYear(), dateTo.getMonth() + 1, 0).getDate();
                  const risks = [];
                  // Check each IB projected vs target
                  sortedIBs.forEach(ib => {
                    const projected = daysPassed > 0 ? (ib.commission || 0) / daysPassed * daysInMonth : 0;
                    const monthlyTarget = (tgt.commission || 0) / Math.max(sortedIBs.length, 1);
                    if (monthlyTarget > 0 && projected < monthlyTarget * 0.7 && (ib.commission || 0) > 0) {
                      risks.push({ name: ib.ib_name, projected: Math.round(projected), target: Math.round(monthlyTarget), shortfall: Math.round(monthlyTarget - projected) });
                    }
                  });
                  // War room critical channels
                  const criticals = (warRoomHQ?.channels || []).filter(c => c.health < 40 && c.has_data);
                  // War room issues
                  const topIssues = (warRoomHQ?.all_issues || []).filter(i => i.severity === 'critical').slice(0, 3);
                  
                  if (risks.length === 0 && criticals.length === 0 && topIssues.length === 0) return null;
                  return (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-px flex-1 bg-[#1a1a25]" />
                        <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#ef4444]">Risk Alerts</span>
                        <div className="h-px flex-1 bg-[#1a1a25]" />
                      </div>
                      <p className="text-[9px] text-[#4b5563] mb-2">Channels requiring immediate attention</p>
                      <div className="space-y-1.5">
                        {risks.map(r => (
                          <div key={r.name} className="flex items-center gap-2 p-2 rounded-lg bg-[#ef4444]/5 border border-[#ef4444]/20">
                            <AlertTriangle size={11} className="text-[#ef4444] shrink-0" />
                            <span className="text-[9px] text-white flex-1">
                              <span className="font-bold">{r.name}</span> — projected RM{r.projected.toLocaleString()} vs target RM{r.target.toLocaleString()} (shortfall RM{r.shortfall.toLocaleString()})
                            </span>
                          </div>
                        ))}
                        {criticals.map(c => (
                          <div key={c.ib_id} className="flex items-center gap-2 p-2 rounded-lg bg-[#ef4444]/5 border border-[#ef4444]/20">
                            <AlertTriangle size={11} className="text-[#ef4444] shrink-0" />
                            <span className="text-[9px] text-white"><span className="font-bold">{c.name}</span> — health score {c.health}/100 (critical)</span>
                          </div>
                        ))}
                        {topIssues.map((iss, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-[#f59e0b]/5 border border-[#f59e0b]/20">
                            <AlertTriangle size={11} className="text-[#f59e0b] shrink-0" />
                            <span className="text-[9px] text-white"><span className="font-bold">{iss.channel}</span> — {iss.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* ──────────── SECTION: Trend Sparklines ──────────── */}
                {(useFiltered ? filteredTrends : data?.daily_trends || []).length > 3 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-px flex-1 bg-[#1a1a25]" />
                      <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#6b7280]">Trend Overview</span>
                      <div className="h-px flex-1 bg-[#1a1a25]" />
                    </div>
                    <p className="text-[9px] text-[#4b5563] mb-2">Daily movement over selected period</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { key: 'commissions', label: 'Commission', color: '#10b981', prefix: 'RM ' },
                        { key: 'leads', label: 'Leads', color: '#3b82f6', prefix: '' },
                        { key: 'subscribers', label: 'Subscribers', color: '#8b5cf6', prefix: '' },
                        { key: 'spend', label: 'Ad Spend', color: '#ef4444', prefix: 'RM ' },
                      ].map(metric => {
                        const trendData = useFiltered ? filteredTrends : (data?.daily_trends || []);
                        const points = trendData.map(d => d[metric.key] || 0);
                        const max = Math.max(...points, 1);
                        const total = points.reduce((s, v) => s + v, 0);
                        const svgW = 120; const svgH = 28;
                        const polyline = points.map((v, i) => `${(i / Math.max(points.length - 1, 1)) * svgW},${svgH - (v / max) * (svgH - 4)}`).join(' ');
                        return (
                          <div key={metric.key} className="p-2.5 rounded-lg bg-[#111118] border border-[#1a1a25]">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-[8px] font-bold uppercase tracking-widest text-[#6b7280]">{metric.label}</span>
                              <span className="text-[9px] font-mono font-bold" style={{color: metric.color}}>{metric.prefix}{total.toLocaleString('en-MY', {maximumFractionDigits:0})}</span>
                            </div>
                            <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} className="w-full">
                              <defs>
                                <linearGradient id={`sg-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={metric.color} stopOpacity="0.3" />
                                  <stop offset="100%" stopColor={metric.color} stopOpacity="0" />
                                </linearGradient>
                              </defs>
                              <polygon points={`0,${svgH} ${polyline} ${svgW},${svgH}`} fill={`url(#sg-${metric.key})`} />
                              <polyline points={polyline} fill="none" stroke={metric.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ──────────── SEPARATOR ──────────── */}
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-[#1a1a25]" />
                  <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#6b7280]">Detailed Breakdown</span>
                  <div className="h-px flex-1 bg-[#1a1a25]" />
                </div>

                {/* All Channels Table */}
                <div>
                  <h4 className="text-[9px] font-bold uppercase tracking-widest text-[#6b7280] mb-2">Channel Breakdown</h4>
                  <div className="overflow-x-auto rounded-xl border border-[hsl(var(--border))]">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="bg-[hsl(var(--surface-elevated))] border-b border-[hsl(var(--border))]">
                          <th className="py-2 px-3 text-left font-bold text-[hsl(var(--text-muted))] uppercase">Channel</th>
                          <th className="py-2 px-2 text-right font-bold text-[hsl(var(--text-muted))] uppercase">Spend</th>
                          <th className="py-2 px-2 text-right font-bold text-[hsl(var(--text-muted))] uppercase">Leads</th>
                          <th className="py-2 px-2 text-right font-bold text-[hsl(var(--text-muted))] uppercase">Closed</th>
                          <th className="py-2 px-2 text-right font-bold text-[hsl(var(--text-muted))] uppercase">Close%</th>
                          <th className="py-2 px-2 text-right font-bold text-[hsl(var(--text-muted))] uppercase">Commission</th>
                          <th className="py-2 px-2 text-right font-bold text-[hsl(var(--text-muted))] uppercase">Lot</th>
                          <th className="py-2 px-2 text-right font-bold text-[hsl(var(--text-muted))] uppercase">Com/Lot</th>
                          <th className="py-2 px-2 text-right font-bold text-[hsl(var(--text-muted))] uppercase">Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedIBs.filter(ib => (ib.commission || 0) > 0 || (ib.leads || 0) > 0).map(ib => {
                          const prevIb = prevIbPerf.find(p => p.ib_id === ib.ib_id);
                          const chg = prevIb ? parseFloat(pct(ib.commission || 0, prevIb.commission || 0)) : 0;
                          const cr = ib.leads > 0 ? ((ib.closed / ib.leads) * 100).toFixed(1) : '0';
                          const ibCpl = (ib.com_per_lot || 0);
                          return (
                            <tr key={ib.ib_id} className="border-b border-[hsl(var(--border))]/30 hover:bg-[hsl(var(--surface-elevated))]">
                              <td className="py-2 px-3 font-semibold text-white">{ib.ib_name}</td>
                              <td className="py-2 px-2 text-right font-mono text-white">RM{(ib.ad_spent || 0).toLocaleString('en-MY', {maximumFractionDigits:0})}</td>
                              <td className="py-2 px-2 text-right font-mono text-white">{ib.leads || 0}</td>
                              <td className="py-2 px-2 text-right font-mono text-white">{ib.closed || 0}</td>
                              <td className="py-2 px-2 text-right font-mono text-white">{cr}%</td>
                              <td className="py-2 px-2 text-right font-mono font-bold text-[#10b981]">RM{(ib.commission || 0).toLocaleString('en-MY', {maximumFractionDigits:0})}</td>
                              <td className="py-2 px-2 text-right font-mono text-[#9ca3af]">{(ib.traded_lot || 0).toLocaleString('en-MY', {maximumFractionDigits:1})}</td>
                              <td className="py-2 px-2 text-right font-mono font-bold">
                                <span className={`${ibCpl >= 12 ? 'text-[#10b981]' : ibCpl >= 8 ? 'text-[#f59e0b]' : ibCpl > 0 ? 'text-[#ef4444]' : 'text-[#6b7280]'}`}>
                                  {ibCpl > 0 ? `$${ibCpl.toFixed(2)}` : '—'}
                                </span>
                              </td>
                              <td className="py-2 px-2 text-right">
                                <span className={`text-[10px] font-bold ${chg >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                                  {chg >= 0 ? '↑' : '↓'}{Math.abs(chg).toFixed(0)}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Export Buttons */}
                <div className="flex items-center gap-3 pt-3 border-t border-[hsl(var(--border))]">
                  <button onClick={() => {
                    const el = document.getElementById('summary-report-content');
                    if (!el) return;
                    // Give the browser one paint tick so all fonts/layouts settle
                    // before capture (fixes: sometimes exported image was rendered
                    // as a linear text stack because grid CSS hadn't applied yet).
                    requestAnimationFrame(() => {
                      import('html2canvas').then(({ default: html2canvas }) => {
                        html2canvas(el, {
                          backgroundColor: '#09090B',
                          scale: 2,
                          useCORS: true,
                          logging: false,
                          // Force explicit dimensions from the element's own layout
                          // so html2canvas doesn't fall back to the viewport width
                          // (which caused stacked-left output on Monthly view when
                          // the report is much wider than the modal viewport).
                          width: el.scrollWidth,
                          height: el.scrollHeight,
                          windowWidth: el.scrollWidth,
                          windowHeight: el.scrollHeight,
                          // Inline critical layout styles into cloned DOM so
                          // Tailwind's `hsl(var(--surface))` variables and grids
                          // survive the cloning process.
                          onclone: (clonedDoc) => {
                            const target = clonedDoc.getElementById('summary-report-content');
                            if (!target) return;
                            target.style.width = `${el.scrollWidth}px`;
                            target.style.maxWidth = 'none';
                            target.style.overflow = 'visible';
                          },
                        }).then(canvas => {
                          const link = document.createElement('a');
                          link.download = `Underads_${summaryType}_report_${format(dateTo, 'yyyyMMdd')}.png`;
                          link.href = canvas.toDataURL('image/png');
                          link.click();
                          toast.success('Report exported as image!');
                        }).catch(err => {
                          console.error('Export failed:', err);
                          toast.error(`Export failed: ${err?.message || 'unknown'}`);
                        });
                      }).catch(() => toast.error('Export failed — install html2canvas'));
                    });
                  }} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#3b82f6] text-white text-xs font-semibold hover:bg-[#2563eb] transition-all" data-testid="export-summary-img">
                    <FileText size={14} /> Export Image
                  </button>
                  <button onClick={() => {
                    const g2 = data?.global_metrics || {};
                    const ibP = data?.ib_performance || [];
                    const sorted = [...ibP].sort((a, b) => (b.commission || 0) - (a.commission || 0));
                    let text = `${summaryType === 'weekly' ? 'WEEKLY' : 'MONTHLY'} PERFORMANCE REPORT\n`;
                    text += `Underads Capital — ${periodLabel}\n\n`;
                    text += `*Executive Summary*\n`;
                    text += `Commission: RM${(g2.total_commission||0).toLocaleString('en-MY',{maximumFractionDigits:0})} (${arrow(g2.total_commission, pg.total_commission)} ${pct(g2.total_commission||0, pg.total_commission||0)}%)\n`;
                    text += `Leads: ${(g2.total_leads||0).toLocaleString()} | Closed: ${(g2.total_closed||0).toLocaleString()}\n`;
                    text += `Closing Rate: ${g2.total_leads > 0 ? ((g2.total_closed/g2.total_leads)*100).toFixed(1) : '0'}%\n`;
                    text += `Ad Spend: RM${(g2.total_spend||0).toLocaleString('en-MY',{maximumFractionDigits:0})}\n`;
                    text += `Deposit: $${(g2.total_deposit||0).toLocaleString('en-MY',{maximumFractionDigits:0})} USD\n`;
                    const gCpl = g2.total_traded_lot > 0 ? (g2.total_commission_usd || (g2.total_commission / 4.2)) / g2.total_traded_lot : 0;
                    if (gCpl > 0) text += `Com/Lot: $${gCpl.toFixed(2)}/lot ${gCpl >= 12 ? '🟢' : gCpl >= 8 ? '🟡' : '🔴'}\n`;
                    text += `\n`;
                    text += `*Channel Rankings*\n`;
                    sorted.filter(ib => (ib.commission||0) > 0).forEach((ib, i) => {
                      const medal = `${i+1}.`;
                      const cr2 = ib.leads > 0 ? ((ib.closed/ib.leads)*100).toFixed(1) : '0';
                      const ibCpl2 = ib.com_per_lot || 0;
                      text += `${medal} ${ib.ib_name}: RM${(ib.commission||0).toLocaleString('en-MY',{maximumFractionDigits:0})} | ${ib.leads||0} leads | ${cr2}% close${ibCpl2 > 0 ? ` | $${ibCpl2.toFixed(2)}/lot` : ''}\n`;
                    });
                    navigator.clipboard.writeText(text).then(() => toast.success('Copied! Paste ke Telegram'));
                  }} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#10b981] text-white text-xs font-semibold hover:bg-[#059669] transition-all" data-testid="copy-summary-btn">
                    <Send size={14} /> Copy & Share
                  </button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      </div>
    </ResponsiveLayout>
  );
}
